import {Component, ElementRef, OnInit, ViewChild} from '@angular/core';
import {FormBuilder, FormControl, FormGroup, Validators} from '@angular/forms';
import {ToastsManager} from 'ng2-toastr';
import { ActivatedRoute, Router } from '@angular/router';
import * as moment from 'moment';
import * as _ from 'lodash';

import {CommonHelper} from '../../../shared/helpers/CommonHelper';
import {LocalStorage} from '../../../shared/wrappers/LocalStorage';
import {GUARDS} from '../../../shared/auth/Roles';

import {
    IPublicConsultantDTO,
    IAccessFlags,
    IFormState,
    IPublicFields,
    IUser,
    customValidators,
    MASKS
} from './interfaces';

import { Responsible } from '../../../models/Responsible.model';
import { UserService } from '../../../services/user.service';
import { ProjectFinancialService } from '../../../services/projectFinancial.service';
import { ConsultantService } from '../../../services/consultant.service';
import { PublicConsultantService } from '../../../services/public-consultant.service';
import { DropdownItemFactory } from '../../../shared/factories/dropdown/dropdown-item.factory';
import { DropdownSettingsFactory } from '../../../shared/factories/dropdown/dropdown-settings.factory';
import { ExcellenceCenter } from "../../../models/ExcellenceCenter.model";
import { ExcellenceCentersService } from "../../../services/excellenceCenters.service";
import { Consultant } from '../../../models/Consultant.model';
import { ErrorHandlerProvider } from '../../../shared/providers/errorHandler.provider';

@Component({
    selector: 'app-public-consultant-edit',
    templateUrl: './public-consultant-edit.component.html',
    styleUrls: ['./public-consultant-edit.component.css']
})
export class PublicConsultantEditComponent implements OnInit {
    @ViewChild("buttonOpenModalInfra") buttonOpenModalInfra: ElementRef;
    @ViewChild('buttonModalContractStatus') buttonModalContractStatus: ElementRef;

    user: IUser;
    consultant: IPublicConsultantDTO;
    consultants: Consultant = new Consultant();
    acceptedTerm: Boolean;
    publicDataForm: FormGroup;
    accessFlags: IAccessFlags;

    loading: boolean = false;
    GUARDS = GUARDS;
    MASKS = MASKS;

    responsibles: Responsible[] = new Array<Responsible>();
    bps: Responsible[] = new Array<Responsible>();
    occupations: any[] = [];
    positions: any[] = [];
    distract: Boolean = false;
    discharge: Boolean = false;
    consultantAux: Consultant = new Consultant();
    typeContractAux: string = '';
    skills: any = [];
    skillsDropdown = { list: [], options: null, selected: [] };
    skillAux: string = ''

    laptopReturnType: number = 0;
    consultantId: string;
    excellenceCenters: Array<ExcellenceCenter> = [];
    supportCenters: Array<ExcellenceCenter> = [];
    allCenters: Array<ExcellenceCenter> = [];
    laptopHasBreakdown: boolean = false;
    updateLaptopAfterReturnAgreement: boolean = false;

    constructor(
        private formBuilder: FormBuilder,
        private route: ActivatedRoute,
        private toastr: ToastsManager,
        private localStorage: LocalStorage,
        public commonHelper: CommonHelper,
        private userService: UserService,
        private errorHandler: ErrorHandlerProvider,
        public consultantService: ConsultantService,
        private publicConsultantService: PublicConsultantService,
        private router: Router,
        private dropDownItemFactory: DropdownItemFactory,
        private dropDownSettingFactory: DropdownSettingsFactory,
        private excellenceCentersService: ExcellenceCentersService
    ) { }

    async ngOnInit() {
        this.loading = true;

        this.checkPermissionsAndUpdateAccessFlags();
        this.buildPublicDataForm();
        await this.loadConsultantPublicInfo();
        this.loading = false;
    }

    checkPermissionsAndUpdateAccessFlags() {
        this.user = this.localStorage.retrieve('user');

        this.accessFlags = {
            isInfra: false,
            canEdit: true,
            canSave: true,
            isConsultantAJuridicalPerson: false,
            isLaptopStatusValid: false,
            isCNPJInvalid: false,
            isBusinessPartner:false,
            isAdministrative: false
        };

        switch(this.user.permission) {
            case GUARDS.INFRA:
                this.accessFlags.isInfra = true;
                this.accessFlags.canEdit = false;
                break;
            case GUARDS.BUSINESSPARTNER:
                this.accessFlags.isBusinessPartner = true;
                this.accessFlags.canEdit = false
                break;
            case GUARDS.ADMINISTRATIVE:
                    this.accessFlags.isAdministrative = true;
                    this.accessFlags.canEdit = false
            default:
                break;
        }
    }

    buildPublicDataForm() {
        const getPublicFields = (): IPublicFields => {
            const defaultState: IFormState = {
                value: '',
                disabled: !this.accessFlags.canEdit
            };

            const getContractGroup = () => {
                const contract = {
                    cnpj:           new FormControl(defaultState, [Validators.required]),
                    companyName:    new FormControl(defaultState, Validators.required),
                    occupation:     new FormControl(defaultState, [Validators.required, customValidators.occupation]),
                    position:       new FormControl(defaultState, [Validators.required, customValidators.position]),
                    kind:   new FormControl(defaultState, [Validators.required, customValidators.kind]),
                }

                const canAccessPrivateData = this.accessFlags.isInfra;

                if(canAccessPrivateData) {
                    const termination = {
                        laptopReturned:         new FormControl(null),
                        cancelEmail:            new FormControl(null),
                        descReturnAgreement:    new FormControl(null),
                        laptopHasBreakdown:     new FormControl(null),
                        damageValueObservation: new FormControl(null),
                        damageValue:            new FormControl(null),
                    };

                    contract["termination"] = new FormGroup({...termination});
                }

                return new FormGroup({...contract});
            }

            const getPersonalGroup = () => {
                const personal = {
                    cpf:    new FormControl(defaultState, [Validators.required]),
                    sex:    new FormControl(defaultState, [Validators.required]),
                }

                return new FormGroup({...personal});
            }

            const contractGroup = getContractGroup();
            const personalGroup = getPersonalGroup();
            if (!this.accessFlags.isInfra && !this.accessFlags.isBusinessPartner) {
                return {
                    name:               new FormControl(defaultState, Validators.required),
                    fcEmail:            new FormControl(defaultState, [Validators.required, Validators.email]),
                    consultantStart:    new FormControl(defaultState, Validators.required),
                    excellenceCenter:   new FormControl(defaultState, [Validators.required, customValidators.excellenceCenter]),
                    supportCenter:      new FormControl(defaultState, [Validators.required, customValidators.supportCenter]),
                    responsible:        new FormControl(defaultState, [Validators.required, customValidators.responsible]),
                    bp:                 new FormControl(defaultState, [Validators.required, customValidators.bp]),
                    acceptedTerm:       new FormControl(defaultState, Validators.required),
                    personal:           personalGroup,
                    contract:           contractGroup
                }
            } else {
                const fields = {
                    name:               new FormControl(defaultState),
                    fcEmail:            new FormControl(defaultState),
                    consultantStart:    new FormControl(defaultState),
                    excellenceCenter:   new FormControl(defaultState),
                    supportCenter:      new FormControl(defaultState),
                    responsible:        new FormControl(defaultState),
                    bp:                 new FormControl(defaultState),
                    acceptedTerm:       new FormControl(defaultState),
                    personal:           personalGroup,
                    contract:           contractGroup
                }

                if(this.accessFlags.isBusinessPartner) {
                    fields["skills"] = new FormControl(null)
                }
                return fields
            }

        }

        const fields = getPublicFields();

        this.publicDataForm = this.formBuilder.group({ ...fields });
    }

    async loadConsultantPublicInfo() {
        this.consultantId = this.route.snapshot.params['id'];

        const loadExcellenceCenterAndSupportCenters = async() => {
            this.allCenters = await this.excellenceCentersService.getAll();

            this.excellenceCenters = this.allCenters.filter(center => center.type == 1 );
            this.excellenceCenters = _.orderBy(this.excellenceCenters, [sup => sup.name.toUpperCase()]);

            this.supportCenters = this.allCenters.filter(center => center.type == 2 );
            this.supportCenters = _.orderBy(this.supportCenters, [sup => sup.name.toUpperCase()]);

        };

        const getConsultant = async () => {
            this.consultant = await this.publicConsultantService.getPublicInfoByConsultant(this.consultantId);
            if(this.consultant.contract) {
                this.accessFlags.isConsultantAJuridicalPerson = this.consultant.contract.kind === 1;

                if(this.consultant.contract.termination) {
                    this.updateLaptopAfterReturnAgreement = this.consultant.contract.termination.returnAgreement ? true : false;
                    const laptopStatus:Boolean = this.consultant.contract.termination.laptopReturned || this.consultant.contract.termination.returnAgreement;
                    this.accessFlags.isLaptopStatusValid = laptopStatus;
                }
            }
        };

        const getSkills = async () => {
            this.skills = await this.consultantService.getAllSkills();
            this.skillsDropdown.options = this.dropDownSettingFactory.build({text:'Skills'});
            this.skillsDropdown.list = this.dropDownItemFactory.build({source:this.skills});
            this.skillsDropdown.selected = this.dropDownItemFactory.build({source:this.consultant.skills});
        }

        const setFormInitialValues = () => {

            const setBasicFields = () => {
                const basicFields = [
                    "name",
                    "fcEmail",
                    "acceptedTerm",
                ];

                basicFields.forEach(fieldName => {
                    const field = this.publicDataForm.get(fieldName);
                    if(field && this.consultant[fieldName] != null) {
                        field.setValue(this.consultant[fieldName]);
                    }
                });
            }

            const setAreaFields = () => {
                if(this.consultant["supportCenter"] != null) {
                    const supportCenterField = this.publicDataForm.get("supportCenter");
                    supportCenterField.setValue(this.consultant["supportCenter"]["_id"]);
                }

                if(this.consultant["excellenceCenter"] != null) {
                    const excellenceCenterField = this.publicDataForm.get("excellenceCenter");
                    excellenceCenterField.setValue(this.consultant["excellenceCenter"]["_id"]);
                }

                if(this.consultant["responsible"] != null) {
                    const responsibleField = this.publicDataForm.get("responsible");
                    responsibleField.setValue(this.consultant["responsible"]["_id"]);
                }

                if(this.consultant["bp"] != null) {
                    const bpField = this.publicDataForm.get("bp");
                    bpField.setValue(this.consultant["bp"]["_id"]);
                }

            }

            const setDateFields = () => {
                const consultantDateFieldNames = [
                    "consultantStart"
                ];

                consultantDateFieldNames.forEach(fieldName => {
                    const field = this.publicDataForm.get(fieldName);
                    if(field && this.consultant[fieldName] != null) {
                        const dateString = this.getStringFromDate(this.consultant[fieldName]);
                        field.setValue(dateString);
                    }
                });
            }

            const setContractFields = () => {

                if(this.consultant.contract) {
                    const contractFields = [
                        "cnpj",
                        "companyName",
                        "occupation",
                        "position",
                        "kind"
                    ];

                    contractFields.forEach(fieldName => {
                        const field = this.publicDataForm.get(["contract", fieldName]);
                        if(field && this.consultant["contract"][fieldName] != null) {
                            field.setValue(this.consultant["contract"][fieldName])
                        }
                    });

                    if(this.consultant.contract.termination) {
                        const contractTerminationFields = [
                            "laptopReturned",
                            "cancelEmail",
                            "descReturnAgreement",
                            "damageValueObservation",
                            "damageValue"
                        ];

                        contractTerminationFields.forEach(fieldName => {
                            const field = this.publicDataForm.get(["contract", "termination", fieldName]);
                            if(field && this.consultant["contract"]["termination"][fieldName] != null) {
                                field.setValue(this.consultant["contract"]["termination"][fieldName])
                            }
                        });
                    }
                }

            }

            const setPersonalFields = () => {
                if(this.consultant.personal) {
                    const personalFields = ["cpf","sex"];

                    personalFields.forEach(fieldName => {
                        const field = this.publicDataForm.get(["personal", fieldName]);
                        if(field && this.consultant["personal"][fieldName] != null) {
                            field.setValue(this.consultant["personal"][fieldName])
                        }
                    });
                }
            }

            setBasicFields();
            setAreaFields();
            setDateFields();
            setContractFields();
            setPersonalFields();
        };

        await getConsultant();
        await loadExcellenceCenterAndSupportCenters();
        await getSkills();

        this.responsibles = await this.userService.getActiveUsers();
        this.bps = [...this.responsibles];
        this.occupations = await this.consultantService.getAllContractProfile();
        this.positions = this.commonHelper.getAllPositionType();

        setFormInitialValues();
    }

    async save() {
        if(this.verifyIfEveryFieldIsValid()) {
            const isEmpty = (obj) => Object.keys(obj).length === 0;
            const isDifferent = (a, b) => a !== b;

            const generatePayload = () => {
                const payload = {};

                const publicFieldNames = [
                    "name",
                    "fcEmail",
                    "acceptedTerm",
                ];

                publicFieldNames.forEach(fieldName => {
                    const field = this.publicDataForm.get(fieldName);
                    if(field && this.consultant[fieldName] != null) {
                        if(isDifferent(field.value, this.consultant[fieldName])) {
                            payload[fieldName] = field.value;
                        }
                    }
                });

                if(this.consultant["supportCenter"] != null) {
                    const supportCenterField = this.publicDataForm.get("supportCenter");
                    if(supportCenterField && isDifferent(supportCenterField.value, this.consultant["supportCenter"]["_id"])) {
                        payload["supportCenter"] = supportCenterField.value;
                    }
                }

                if(this.consultant["excellenceCenter"] != null) {
                    const excellenceCenterField = this.publicDataForm.get("excellenceCenter");
                    if(excellenceCenterField && isDifferent(excellenceCenterField.value, this.consultant["excellenceCenter"]["_id"])) {
                        payload["excellenceCenter"] = excellenceCenterField.value;
                    }
                }

                if(this.consultant["responsible"] != null) {
                    const responsibleField = this.publicDataForm.get("responsible");
                    if(responsibleField && isDifferent(responsibleField.value, this.consultant["responsible"]["_id"])) {
                        payload["responsible"] = responsibleField.value;
                    }
                }

                if(this.consultant["bp"] != null) {
                    const bpField = this.publicDataForm.get("bp");
                    if(bpField && isDifferent(bpField.value, this.consultant["bp"]["_id"])) {
                        payload["bp"] = bpField.value;
                    }
                }

                if(this.consultant["skills"] && this.consultant["skills"].length > 0) {
                    payload["skills"] = [...this.consultant["skills"]]
                }

                const dateFieldNames = [
                    "consultantStart"
                ];

                dateFieldNames.forEach(fieldName => {
                    const field = this.publicDataForm.get(fieldName);
                    if(field && this.consultant[fieldName] != null) {
                        const dateString = this.getStringFromDate(this.consultant[fieldName]);
                        if(isDifferent(field.value, dateString)) {
                            const dateFromString = this.getDateFromString(field.value);
                            if(dateFromString != null) {
                                payload[fieldName] = dateFromString;
                            }
                        }
                    }
                });

                if(this.consultant.contract) {
                    payload["contract"] = {};

                    const contractFieldNames = [
                        "cnpj",
                        "companyName",
                        "occupation",
                        "position",
                        "kind"
                    ];

                    contractFieldNames.forEach(fieldName => {
                        const field = this.publicDataForm.get(["contract", fieldName]);
                        if(field && this.consultant["contract"][fieldName] != null) {
                            if(isDifferent(field.value, this.consultant["contract"][fieldName])) {
                                payload["contract"][fieldName] = field.value;
                            }
                        }
                    });

                    if(this.consultant.contract.termination) {
                        payload["contract"]["termination"] = {};

                        const contractTerminationFieldNames = [
                            "laptopReturned",
                            "cancelEmail",
                            "descReturnAgreement",
                            "damageValueObservation",
                            "damageValue"
                        ];

                        contractTerminationFieldNames.forEach(fieldName => {
                            const field = this.publicDataForm.get(["contract", "termination", fieldName]);
                            if(field) {
                                if(fieldName == "cancelEmail" || fieldName == "descReturnAgreement" || fieldName == "damageValueObservation" || fieldName == "damageValue") {
                                    if(fieldName == "cancelEmail" || fieldName == "descReturnAgreement") {
                                        if(field.value != "" && isDifferent(field.value, this.consultant["contract"]["termination"][fieldName])) {
                                            payload["contract"]["termination"][fieldName] = field.value;
                                        }
                                    } else if(this.consultant["contract"]["termination"][fieldName] != null) {
                                        if(isDifferent(field.value, this.consultant["contract"]["termination"][fieldName])) {
                                            payload["contract"]["termination"][fieldName] = field.value;
                                        }
                                    }
                                }
                            }
                        });

                        if(isEmpty(payload["contract"]["termination"])) {
                            delete payload["contract"]["termination"];
                        }
                    }

                    if(isEmpty(payload["contract"])) {
                        delete payload["contract"];
                    }
                }

                return payload;
            }

            const notifyDepartment = async () => {
                const departamentAndTypes = {
                    departament: {
                        judiciary: 2,
                        divisionResponsible: [2, 3, 4]
                    },
                    type: {
                        noType: 0,
                        additiveValue: 1,
                        laptopReturn: 2,
                        changeCnpjOrCompanyName: 3
                    }
                };

                if (this.accessFlags.isInfra) {
                    const fieldDescReturnAgreement = this.publicDataForm.get(["contract", "termination", "descReturnAgreement"]);
                    if(fieldDescReturnAgreement && this.laptopReturnType == 3) {
                        const descReturnAgreement = fieldDescReturnAgreement.value;
                        this.consultant.contract.termination.descReturnAgreement = descReturnAgreement;
                    }

                    const fieldDamageValueObservation = this.publicDataForm.get(["contract", "termination", "damageValueObservation"]);
                    if(fieldDamageValueObservation && this.laptopReturnType == 2 && this.laptopHasBreakdown) {
                        const damageValueObservation = fieldDamageValueObservation.value;
                        this.consultant.contract.termination.damageValueObservation = damageValueObservation;
                        this.consultant.contract.termination.laptopHasBreakdown = true;
                    }

                    const fieldDamageValue = this.publicDataForm.get(["contract", "termination", "damageValue"]);
                    if(fieldDamageValue && this.laptopReturnType == 2 && this.laptopHasBreakdown) {
                        const damageValue = fieldDamageValue.value;
                        this.consultant.contract.termination.damageValue = damageValue;
                    }

                    if(this.laptopHasBreakdown && (!this.consultant.contract.termination.damageValue ||
                        !this.consultant.contract.termination.damageValueObservation)) {
                            this.loading = false;
                            return this.toastr.warning('Ao marcar a opção "Laptop foi devolvido com avarias", é obrigatório inserir o "Valor da avaria" e as suas respectivas "observações".');
                    }

                    if(this.laptopReturnType > 1)
                        await this.consultantService.notifyDP(this.consultant, departamentAndTypes.departament.judiciary, this.laptopReturnType);

                    if(!this.consultant.contract.termination.returnAgreement) {
                        this.toastr.success('Devolução de Laptop confirmada ou acordada');
                        this.toastr.success('Um e-mail foi enviado para o responsável');
                    };
                    this.loading = false;
                    this.router.navigate(['/consultants']);
                };
            }

            const payload = generatePayload();

            if(this.accessFlags.isInfra) {
                this.loading = true;
                return await notifyDepartment();
            } else {
                if(!isEmpty(payload)) {
                    this.loading = true;
                    const response = await this.publicConsultantService.updateOnlyPublicFields(this.consultantId, payload)
                    this.loading = false;
                    if(response._id){
                       this.toastr.success('Usuário alterado com sucesso.')
                       this.router.navigate(['/consultants']);
                    } else  {
                        let msg = JSON.parse(response._body);
                        this.toastr.error(msg.message)
                    }
                } else {
                    this.toastr.warning('Nenhuma alteração foi realizada.');
                }
            }
        }
    }

    onChangeInputText(inputName: string) {
        const nameField = this.publicDataForm.get(inputName);

        if(nameField) {
            const newValue = (nameField.value as string).trim();
            nameField.setValue(newValue);
        }
    }

    onChangeInputDate(inputName: string) {
        const datePattern = /^(0[1-9]|1[0-9]|2[0-9]|3[01]).(0[1-9]|1[012]).[0-9]{4}$/i;

        const dateField = this.publicDataForm.get(inputName);
        if(dateField) {
            if(!(new RegExp(datePattern).test(dateField.value))) {
                dateField.setValue("");
                this.toastr.warning(`Digite uma data válida.`,"Data inválida!");
            }
        }
    }

    openModalContractStatus(c: any, type: string) {
        this.typeContractAux = type;
        this.consultantAux = { ...c };
        this.buttonModalContractStatus.nativeElement.click();
    }

    async close() {
        this.consultantAux = new Consultant();
        this.typeContractAux = '';
    }

    onChangeInputLaptopReturnType(type: number) {
        this.laptopHasBreakdown = false;
        const laptopReturnTypeField = this.publicDataForm.get(["contract", "termination", "laptopReturned"]);
        if(laptopReturnTypeField) {
            switch(type) {
                case 1:
                    laptopReturnTypeField.setValue(false);
                    this.laptopReturnType = 1;
                    break;
                case 2:
                    laptopReturnTypeField.setValue(true);
                    this.laptopReturnType = 2;
                    break;
                case 3:
                    this.laptopReturnType = 3;
                    break;
                default:
                    this.laptopReturnType = 4;
                    break;
            }
        }

        if(this.laptopReturnType != 3 && !this.updateLaptopAfterReturnAgreement){
            this.publicDataForm.get(["contract", "termination", "descReturnAgreement"]).setValue("");
        }

        if(this.laptopReturnType != 2){
            this.publicDataForm.get(["contract", "termination", "damageValue"]).setValue("");
            this.publicDataForm.get(["contract", "termination", "damageValueObservation"]).setValue("");
        }
    }

    async onChangeInputEmail() {
        this.loading = true;
        const fcEmail = this.publicDataForm.get("fcEmail");
        if(fcEmail) {
            fcEmail.setValue(fcEmail.value.trim());

            const response = await this.publicConsultantService.verifyIfEmailExists(fcEmail.value);

            if(response.error) {
                this.toastr.error(response.error.message);
                fcEmail.setValue("");
            } else if(response.emailExists) {
                this.toastr.warning(`E-mail: ${fcEmail.value}, já está em uso!`);
                fcEmail.setValue("");
            } else {
                this.toastr.success('E-mail Fcamara está válido.');
            }
        }
        this.loading = false;
    }

    verifyIfEveryFieldIsValid() {
        const basicFieldNames = [
            "name",
            "fcEmail",
            "consultantStart",
            "excellenceCenter",
            "supportCenter",
            "responsible",
            "bp",
            "acceptedTerm"
        ];

        let isValid = true;

        basicFieldNames.forEach(fieldName => {
            const field = this.publicDataForm.get(fieldName);
            if(field && !this.accessFlags.isInfra && this.publicDataForm.get(fieldName).valid)
                isValid = false;
        });

        if(isValid && this.consultant.contract ) {
            const contractFieldNames = [
                "cnpj",
                "companyName",
                "occupation",
                "position",
                "kind"
            ];

            contractFieldNames.forEach(fieldName => {
                const field = this.publicDataForm.get(["contract", fieldName]);
                if(field && !this.accessFlags.isInfra && this.publicDataForm.get(["contract", fieldName]).valid)
                    isValid = false;
            });

            if(isValid && this.consultant.contract.termination) {
                const contractTerminationFieldNames = [
                    "laptopReturned",
                    "cancelEmail",
                    "descReturnAgreement",
                    "damageValueObservation",
                    "damageValue"
                ];

                contractTerminationFieldNames.forEach(fieldName => {
                    const field = this.publicDataForm.get(["contract", "termination", fieldName]);
                    if(field && !this.accessFlags.isInfra && this.publicDataForm.get(["contract", "termination", fieldName]).valid)
                        isValid = false;
                });
            }
        }

        const canSave = isValid && !this.accessFlags.isCNPJInvalid;

        if(this.accessFlags.isBusinessPartner){
            return true
        }

        if(!canSave) {
            this.toastr.warning("Existem campos invalidos.");
        }

        return canSave;
    }

    getStringFromDate(date: Date) {
        return moment(date).format("DD/MM/YYYY");
    }

    getDateFromString(dateString: string) {
        const momentDate = moment(dateString, "DD/MM/YYYY");
        if(momentDate.isValid()){
            return momentDate.toDate();
        } else {
            return null;
        }
    }

    applyErrorStyle(fieldName: string) {
        let isValid = true;

        const field = this.publicDataForm.get(fieldName);
        if(field) {
            isValid = this.publicDataForm.get(fieldName).valid;
        }

        return {
            'has-error': !isValid
        }
    }

    changeSkill(){
        if(this.consultant.skills.length <= this.skillsDropdown.selected.length){
            let diff = _.differenceWith(this.skillsDropdown.selected, this.consultant.skills, (a,b)=> a.id == b._id )
            this.consultant.skills.push({
                _id:diff[0].id,
                name:diff[0].itemName
            })
        }
        else {
            let diff = _.differenceWith(this.consultant.skills,this.skillsDropdown.selected, (a,b)=> a._id == b.id )
            _.remove(this.consultant.skills, item => item._id == diff[0]._id)
        }
    }

    async addSkill() {
        try {
            let resp = await this.consultantService.createSkill(this.skillAux.toUpperCase());
            if(resp._id){
                this.skillsDropdown.list = [
                    ...this.skillsDropdown.list,
                    { id: resp._id, itemName: resp.name }
                ]
                this.toastr.success('Skill criada com sucesso');
            }
            this.clearSkill();
        } catch(err) {
            console.log(err);
            this.toastr.success('Erro ao criar Skill. Tente novamente');
        }
    }

    clearSkill() {
        this.skillAux = '';
    }
}



// WEBPACK FOOTER //
// ./src/app/views/consultants/public-consultant-edit/public-consultant-edit.component.ts